import { useId, useMemo } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@monksflow/monks-ui';
import { Check, ChevronDown } from 'lucide-react';
import type { Channel } from './CopyGenerationOptions.types';
import { useTranslation } from 'react-i18next';

type ChannelOption = { readonly label: string; readonly value: Channel };

type CopyChannelMultiSelectProps = {
  value: Channel[];
  onChange: (next: Channel[]) => void;
  options: readonly ChannelOption[];
  disabled?: boolean;
  'aria-labelledby': string;
};

const VISIBLE_CHIPS = 2;

export const CopyChannelMultiSelect = ({
  value,
  onChange,
  options,
  disabled,
  'aria-labelledby': ariaLabelledBy,
}: CopyChannelMultiSelectProps) => {
  const { t } = useTranslation('chats');
  const selectionId = useId();

  // `options` is a module-scope table whose identity never changes, so without `t` the
  // resolved labels would survive a language change.
  const labelByValue = useMemo(() => new Map(options.map((o) => [o.value, o.label])), [options, t]);

  const visible = value.slice(0, VISIBLE_CHIPS);
  const overflow = value.length - visible.length;

  const toggle = (channel: Channel) => {
    if (value.includes(channel)) {
      // Keep at least one selected to mirror the default-channel invariant.
      if (value.length === 1) return;
      onChange(value.filter((c) => c !== channel));
    } else {
      onChange([...value, channel]);
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild disabled={disabled}>
        <button
          type="button"
          disabled={disabled}
          aria-labelledby={ariaLabelledBy}
          aria-describedby={selectionId}
          className="relative flex items-center gap-2 w-full min-h-9 px-3 py-1.5 rounded-lg border border-border bg-card text-left text-sm disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <div id={selectionId} className="flex flex-1 flex-wrap items-center gap-1.5 pr-5">
            {visible.length === 0 ? (
              <span className="text-muted-foreground text-xs">
                {t('chats.copyChannelMultiSelect.selectChannels')}
              </span>
            ) : (
              visible.map((v) => (
                <span
                  key={v}
                  className="inline-flex items-center px-2 py-1 rounded-lg bg-muted text-xs text-foreground"
                >
                  {labelByValue.get(v) ?? v}
                </span>
              ))
            )}
            {overflow > 0 && (
              <span className="inline-flex items-center px-2 py-1 rounded-lg bg-muted text-xs text-foreground">
                +{overflow}
              </span>
            )}
          </div>
          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={4}
        className="p-1 w-(--radix-popover-trigger-width) min-w-48"
      >
        {options.map((option) => {
          const selected = value.includes(option.value);
          return (
            <button
              key={option.value}
              type="button"
              aria-pressed={selected}
              onClick={() => toggle(option.value)}
              className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-left hover:bg-accent hover:text-accent-foreground focus:outline-none focus-visible:bg-accent"
            >
              <span className="flex h-4 w-4 items-center justify-center">
                {selected && <Check className="h-3.5 w-3.5" />}
              </span>
              <span className="flex-1">{option.label}</span>
            </button>
          );
        })}
      </PopoverContent>
    </Popover>
  );
};
